import React, { FunctionComponent } from 'react';
import { DateUtils } from 'record-it-utils/utils/DateUtils/DateUtils';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import {
  InputLayout,
  InputLayoutProps
} from '../../input/InputLayout/InputLayout';
import sharedStyles from '../../input/sharedStyles.module.css';
import styles from './DateInput.module.css';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useFixedLengthNumberInput } from '../../hooks/useFixedLengthNumberInput/useFixedLengthNumberInput';
import { useMultiElementIsFocused } from '../../hooks/useMultiElementIsFocused/useMultiElementIsFocused';

export type DateInputProps = PropsWithoutChildren<
  Omit<
    InputLayoutProps,
    'onInput' | 'onChange' | 'onInputCapture' | 'onChangeCapture'
  >
> & {
  value: Date | null;
  onInput: (value: Date | null) => void;
  onInputFinished?: () => void;
};

export const DateInput: FunctionComponent<DateInputProps> = React.memo(
  (props) => {
    const { value, onInput, onInputFinished, ...remainingProps } = prepareProps(
      { props, ignoreChildren: true }
    );

    const {
      isFocused: anyInputIsFocused,
      focusHandlerFactory,
      blurHandlerFactory
    } = useMultiElementIsFocused({
      onBlur: () => {
        let bufferedDate = null;
        if (
          dayInputBufferedValue != null &&
          monthInputBufferedValue != null &&
          yearInputBufferedValue != null
        ) {
          bufferedDate = DateUtils.withCurrentTimezone().createDateFromParts({
            year: yearInputBufferedValue,
            month: monthInputBufferedValue,
            day: dayInputBufferedValue,
            hours: value?.getHours() ?? 12,
            minutes: value?.getMinutes() ?? 0,
            seconds: value?.getSeconds() ?? 0,
            milliseconds: value?.getMilliseconds() ?? 0
          });
        }

        if (bufferedDate?.getTime() !== value?.getTime()) {
          onInput(bufferedDate);
        }
      }
    });

    const {
      inputProps: yearInputProps,
      bufferedValue: yearInputBufferedValue,
      focus: focusYearInput
    } = useFixedLengthNumberInput({
      value: value?.getFullYear() ?? null,
      anyInputIsFocused,
      valueLength: 4,
      onValueLengthReached: onInputFinished,
      onFocus: focusHandlerFactory('YearInput'),
      onBlur: blurHandlerFactory('YearInput')
    });

    const handleMonthInputValueLengthReached = useEventCallback(() => {
      focusYearInput();
    });

    const {
      inputProps: monthInputProps,
      bufferedValue: monthInputBufferedValue,
      focus: focusMonthInput
    } = useFixedLengthNumberInput({
      value: value ? value.getMonth() + 1 : null,
      anyInputIsFocused,
      valueLength: 2,
      onValueLengthReached: handleMonthInputValueLengthReached,
      onFocus: focusHandlerFactory('MonthInput'),
      onBlur: blurHandlerFactory('MonthInput')
    });

    const handleDayInputValueLengthReached = useEventCallback(() => {
      focusMonthInput();
    });

    const { inputProps: dayInputProps, bufferedValue: dayInputBufferedValue } =
      useFixedLengthNumberInput({
        value: value?.getDate() ?? null,
        anyInputIsFocused,
        valueLength: 2,
        onValueLengthReached: handleDayInputValueLengthReached,
        onFocus: focusHandlerFactory('DayInput'),
        onBlur: blurHandlerFactory('DayInput')
      });

    return (
      <InputLayout {...remainingProps}>
        {() => (
          <div className={styles.inputsWrapper}>
            <input
              type="text"
              className={classes(sharedStyles.inputBaseStyle, styles.input)}
              {...dayInputProps}
              data-testid="DateInput.DayInput"
            ></input>
            <div className={styles.dateSeparator}>.</div>
            <input
              type="text"
              className={classes(sharedStyles.inputBaseStyle, styles.input)}
              {...monthInputProps}
              data-testid="DateInput.MonthInput"
            ></input>
            <div className={styles.dateSeparator}>.</div>
            <input
              type="text"
              className={classes(sharedStyles.inputBaseStyle, styles.input)}
              {...yearInputProps}
              data-testid="DateInput.YearInput"
            ></input>
          </div>
        )}
      </InputLayout>
    );
  }
);
